import { TheWestSection } from '@news-mono/common'
import {
    PublicationType,
    useFeature,
    Block,
    AllEvents,
    click_origin_breach_screen,
} from '@news-mono/web-common'
import { RequiredAccessLevel } from '@west-australian-newspapers/publication-types'
import React, { useState, useEffect, useCallback } from 'react'
import { useSection } from '../../../../__helpers'
import { MessageBannerSmall } from '../../../../banners/MessageBannerSmall/MessageBannerSmall'
import { useFeatureForRegionalSections } from '../../../../feature-toggling'
import { AbTestExperiment } from '../../abtesting/AbTestingProvider'
import {
    useUpdateBreachWallContext,
    SubscriptionPackages,
    rrmePackageOfferDetails,
} from '../../common'
import {
    BreachWallStyle,
    BreachWallContentType,
} from '../../helpers/BreachWallTypes'
import BreachWallContent from '../BreachWallContent/BreachWallContent'
import {
    StyledTitle,
    StyledTerms,
    StyledLiveTrailerWrap,
    StyledTitleImage,
    StyledBlackFridayTape,
    StyledTerms2022,
} from '../BreachWallContent/BreachWallContent.styled'
import { VideoTrailerCard } from '../../../../cards/VideoTrailerCard/VideoTrailerCard'
import { BreachWallDisplayValues, getBreachWallOptions } from '../../helpers'
import { Logger } from '@west-australian-newspapers/typescript-log'
import H from 'history'
import debug from 'debug'
import { LinkContext } from '../../../../navigation'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import { useAuthActions } from '../../../authentication'
import { BreachWallTerms } from '../BreachWallTerms/BreachWallTerms'

const breachWallDebug = debug('breach-wall')

export const defaultBannerText =
    'Subscribers with digital access can view this article.'

export const defaultCreative = 'TW_Article_Breach_WestBlue_Design_PhotoPackages'
export const defaultStyle: BreachWallStyle = 'default'
export const defaultDeal = ''
export const defaultType: BreachWallContentType = 'default'

export type BreachWallOptions = {
    canShowSportBreach: boolean
    canShowBreachDiscountFootyLivesHere: boolean
    canShowBreachDiscount50sch: boolean
    canShowBlackFriday: boolean
    canShowEofyDiscount: boolean
    canShowBusinessPackDiscount: boolean
    canShowSportPackDiscount: boolean
    canShowAnnualPackage: boolean
    canShowSummerOfCricketBreach: boolean
}

export type BannerSize = 'small' | 'large'

export interface BreachWallContentWrapperProps {
    // Supplied events to utilise, else if not provided it'll utilise defaults
    suppliedDeal?: string
    suppliedType?: BreachWallContentType
    suppliedCreative?: string
    suppliedStyle?: BreachWallStyle
    // Supplied AB testing parameters, else utilise new parameters
    suppliedExperiment?: AbTestExperiment
    // Publication data to update breach wall context
    publication?: PublicationType
    requiredAccess?: RequiredAccessLevel
    location: H.Location
    // Consume all events
    onEvent: (event: AllEvents) => void
    // Values to set the top "banner" values
    bannerSize?: BannerSize
    bannerText?: string
    // Overriding values used from within the breach screen itself
    title?: string
    message?: string
    terms?: string
    log: Logger
    blocks?: Block[]
    forceLoad?: boolean
    isTest?: boolean
    // Optional properties that can be passed on a per-campaign basis
    contentOptions?: {
        [key: string]: any
    }
}

export const BreachWallContentWrapper: React.FC<BreachWallContentWrapperProps> =
    ({
        suppliedDeal,
        suppliedType,
        suppliedCreative,
        suppliedStyle,
        publication,
        requiredAccess,
        onEvent,
        bannerText,
        terms,
        log,
        forceLoad,
        isTest,
        contentOptions,
        location,
    }) => {
        const { onSubscribeClick } = useAuthActions()
        const section = useSection<TheWestSection>()
        const isRegionalOverride = useFeatureForRegionalSections(
            'breach-regional-override',
            section,
        )

        const breachWallToggles = useBreachWallOptions()

        const [isLoading, setLoading] = useState(
            forceLoad === undefined ? true : !forceLoad,
        )

        const shouldShowLiveSportsTrailer =
            contentOptions && contentOptions.showLiveSportTrailer

        // Store breach display values
        const [breachDisplayValues, setBreachDisplayValues] =
            useState<BreachWallDisplayValues>({
                deal: suppliedDeal || defaultDeal,
                type: suppliedType || defaultType,
                style: suppliedStyle || defaultStyle,
            })
        const {
            deal: breachDeal,
            type: breachType,
            style: breachStyle,
        } = breachDisplayValues

        const breachCreative = suppliedCreative || defaultCreative

        useEffect(() => {
            if (!isLoading) return

            // force load is used and so it's probably coming from a test
            // so we don't want it to do any calculation, as it should be
            // supplied with everything it needs
            if (forceLoad === true) {
                return
            }

            const breachWallStyles = getBreachWallOptions(
                location,
                breachWallToggles,
            )
            if (breachWallStyles !== undefined) {
                setBreachDisplayValues(breachWallStyles)
            }

            setLoading(false)
        }, [forceLoad, isLoading, location, breachWallToggles])

        // handle whenever 'subscription options' is clicked.
        const onAllPackagesClicked = useCallback(() => {
            createLinkClickEvent(breachCreative, breachDeal, onEvent)()
            onSubscribeClick({
                callToAction: click_origin_breach_screen,
                breachScreenType: 'Premium - Inline',
            })
        }, [breachCreative, breachDeal, onEvent, onSubscribeClick])

        // fire breach wall context update
        useUpdateBreachWallContext({
            publication: publication,
            requiredAccessLevel: requiredAccess || 'subscriber',
            breachDeal: breachDeal,
            breachCreativeName: breachCreative,
        })

        let subscriptionPackages: SubscriptionPackages = []

        const contentType =
            breachDeal === 'epaper-regional'
                ? 'digital-only'
                : breachType
                ? breachType
                : defaultType

        subscriptionPackages =
            rrmePackageOfferDetails(contentType, log) ??
            rrmePackageOfferDetails(defaultType, log)

        // some logging so that we have relevant information on the breach screen available.
        if (!isLoading || forceLoad) {
            breachWallDebug(
                'Breach screen type has been determined, now about to generate a BreachWallContent with the following values:',
            )
            breachWallDebug('ContentType:', breachType)
            breachWallDebug('Style:', breachStyle)
            breachWallDebug('Deal:', breachDeal)
        }

        // Debug the subscriptionPackages being pulled through
        breachWallDebug(
            {
                subscriptionPackages,
            },
            'BreachWallContentWrapper render with values:',
        )

        return (
            <>
                <div data-testid="BreachScreen">
                    {!isLoading && (
                        <BreachWallContent
                            breachCreativeName={breachCreative}
                            breachType={breachType}
                            breachDeal={breachDeal}
                            breachStyle={breachStyle}
                            isTest={isTest}
                            onEvent={onEvent}
                            title={() => {
                                let img

                                switch (breachType) {
                                    case 'black-friday':
                                        img = require('../../assets/thewest-banner-black-friday.png')
                                        return (
                                            <>
                                                <StyledTitleImage
                                                    breachType={breachType}
                                                    src={img}
                                                    alt={
                                                        'black friday sale 50% off'
                                                    }
                                                />
                                                <StyledTitle
                                                    breachType={breachType}
                                                >
                                                    The No. 1 source of news for
                                                    every West Australian
                                                </StyledTitle>
                                            </>
                                        )
                                    case 'subs-campaign-eofy-weekend-digital':
                                        img = require('../../assets/thewest-digital-hero-variation-eofy-banner.png')
                                        return (
                                            <StyledTitleImage
                                                src={img}
                                                alt={
                                                    'winter sale, 50% off new subscriptions'
                                                }
                                            />
                                        )
                                    case '50-off-afl-breach-screen':
                                        img = require('../../assets/afl_50_banner.png')
                                        return (
                                            <StyledTitleImage
                                                src={img}
                                                alt={
                                                    '50% off all new subscriptions'
                                                }
                                            />
                                        )
                                    case 'afl-footy-lives-here-breach-screen':
                                        img = require(`../../assets/WA_footy_lives_here_logo.png`)
                                        return (
                                            <StyledTitleImage
                                                breachType={breachType}
                                                src={img}
                                                alt={
                                                    'WA Footy Lives Here Banner'
                                                }
                                            />
                                        )
                                    case 'summer-of-cricket':
                                        img = require('../../assets/thewest-banner-summer-of-cricket.png')
                                        return (
                                            <StyledTitleImage
                                                src={img}
                                                breachType={breachType}
                                                alt={
                                                    'summer of cricket 50% off'
                                                }
                                            />
                                        )
                                    case 'default-annual-package':
                                    default:
                                        return (
                                            <StyledTitle
                                                breachType={breachType}
                                            >
                                                The No. 1 source of news for
                                                every West Australian
                                            </StyledTitle>
                                        )
                                }
                            }}
                            footer={() => null}
                            terms={() => {
                                // Utilise the new Breach Terms layout here.
                                if (breachType === 'default') {
                                    return <BreachWallTerms />
                                }

                                // TODO: Remove these legacy terms after a period of time, as the new layout should be utilised for all breach types.
                                if (terms) {
                                    return <StyledTerms>{terms}</StyledTerms>
                                } else {
                                    const initialText =
                                        breachType ===
                                        'afl-footy-lives-here-breach-screen'
                                            ? `See other `
                                            : `*See other `

                                    return (
                                        <LinkContext.Consumer>
                                            {() => (
                                                <StyledTerms2022
                                                    breachType={breachType}
                                                >
                                                    {initialText}
                                                    <a
                                                        href="#"
                                                        onClick={
                                                            onAllPackagesClicked
                                                        }
                                                    >
                                                        subscription options
                                                    </a>{' '}
                                                    and{' '}
                                                    <a
                                                        href={
                                                            'https://subscriber.thewest.com.au/subscription-terms'
                                                        }
                                                        target="_blank"
                                                    >
                                                        Terms &amp; Conditions
                                                    </a>
                                                </StyledTerms2022>
                                            )}
                                        </LinkContext.Consumer>
                                    )
                                }
                            }}
                            //Media banner appears above the title
                            mediaBanner={() => {
                                if (shouldShowLiveSportsTrailer) {
                                    return (
                                        <StyledLiveTrailerWrap>
                                            <VideoTrailerCard
                                                layoutMode="double"
                                                onEvent={() => null}
                                            />
                                        </StyledLiveTrailerWrap>
                                    )
                                }
                                if (breachStyle === 'black-friday') {
                                    const tape = require('../../assets/thewest-tape-black-friday-1.png')

                                    return (
                                        <>
                                            <StyledBlackFridayTape
                                                src={tape}
                                                className="left-tape"
                                            />
                                            <StyledBlackFridayTape
                                                src={tape}
                                                className="right-tape"
                                            />
                                            <StyledBlackFridayTape
                                                src={tape}
                                                className="bottom-left-tape"
                                            />
                                            <StyledBlackFridayTape
                                                src={tape}
                                                className="bottom-right-tape"
                                            />
                                        </>
                                    )
                                }

                                return null
                            }}
                            //Breach banner: "Subscribers with digital access can view this article."
                            breachBanner={() => {
                                if (
                                    breachStyle === 'summer-of-cricket' ||
                                    breachStyle === 'footy-lives-here' ||
                                    breachStyle === 'default'
                                ) {
                                    return null
                                }

                                return (
                                    <MessageBannerSmall
                                        icon="subscriber"
                                        onEvent={onEvent}
                                        isLightText={
                                            breachStyle === 'black-friday' ||
                                            breachStyle === 'eofy'
                                        }
                                        breachStyle={breachStyle}
                                    >
                                        <p>{bannerText || defaultBannerText}</p>
                                    </MessageBannerSmall>
                                )
                            }}
                            subscriptionPackages={subscriptionPackages}
                            isRegionalOverride={isRegionalOverride}
                        />
                    )}
                </div>
            </>
        )
    }

function useBreachWallOptions(): BreachWallOptions {
    return {
        canShowSportBreach: useFeature('sport-breach-screen-variant'),
        canShowBreachDiscount50sch: useFeature('top-50-schools'),
        canShowBlackFriday: useFeature('black-friday'),
        canShowEofyDiscount: useFeature('subs-campaign-eofy-weekend-digital'),
        canShowBusinessPackDiscount: useFeature(
            'subs-campaign-business-pack-2022',
        ),
        canShowSportPackDiscount: useFeature('subs-campaign-sport-pack-2022'),
        canShowBreachDiscountFootyLivesHere: useFeature(
            'afl-footy-lives-here-breach-screen',
        ),
        canShowAnnualPackage: useFeature('breach-show-annual-package'),
        canShowSummerOfCricketBreach: useFeature(
            'summer-of-cricket-breach-screen',
        ),
    }
}

BreachWallContentWrapper.displayName = 'BreachWallContentWrapper'

export default BreachWallContentWrapper
